/**
 * Canvas — Main canvas engine
 * Per PLAN.md core/Canvas.js
 */

import { Viewport } from './Viewport.js';
import { eventBus, Events } from './EventBus.js';
import { GridRenderer } from '../render/GridRenderer.js';
import { ConnectionRenderer } from '../render/ConnectionRenderer.js';
import { SelectionRenderer } from '../render/SelectionRenderer.js';
import { NodeRenderer } from '../render/NodeRenderer.js';
import { THEME } from '../utils/constants.js';

export class Canvas {
    /**
     * Create the canvas engine
     * @param {HTMLCanvasElement} canvasElement 
     * @param {HTMLElement} container 
     */
    constructor(canvasElement, container) {
        this.canvas = canvasElement;
        this.ctx = canvasElement.getContext('2d');
        this.container = container;

        // Viewport
        this.viewport = new Viewport();

        // Renderers
        this.gridRenderer = new GridRenderer();
        this.connectionRenderer = new ConnectionRenderer();
        this.selectionRenderer = new SelectionRenderer();
        this.nodeRenderer = new NodeRenderer();

        // State
        this.document = null;
        this.selectedNodeIds = new Set();
        this.selectedConnectionId = null;
        this.isDark = false;
        this.showGrid = true;

        // Interaction state
        this.isPanning = false;
        this.isDrawingConnection = false;
        this.connectionPreview = null;
        this.selectionBox = null;

        // Animation frame
        this.animationFrameId = null;
        this.needsRender = true;
        this.nodesNeedUpdate = true; // Track if nodes need full re-render

        // Initialize
        this.init();
    }

    /**
     * Initialize canvas
     */
    init() {
        // Set up high-DPI canvas
        this.resize();

        // Initialize node renderer
        this.nodeRenderer.init(this.container);

        // Listen for resize
        window.addEventListener('resize', () => this.resize());

        // Listen for render requests
        eventBus.on(Events.RENDER_REQUEST, () => {
            this.needsRender = true;
        });

        // Listen for theme changes
        eventBus.on(Events.THEME_CHANGE, ({ theme }) => {
            this.isDark = theme === THEME.DARK;
            this.needsRender = true;
        });

        // Listen for grid toggle
        eventBus.on(Events.GRID_TOGGLE, ({ showGrid }) => {
            this.showGrid = showGrid;
            this.needsRender = true;
        });

        // Listen for node changes to trigger full re-render
        eventBus.on(Events.NODE_CREATE, () => this.markNodesChanged());
        eventBus.on(Events.NODE_DELETE, () => this.markNodesChanged());
        eventBus.on(Events.NODE_UPDATE, () => this.markNodesChanged());

        // Set up input handlers
        this.setupInputHandlers();

        // Start render loop
        this.startRenderLoop();
    }

    /**
     * Set up mouse/touch/keyboard input handlers
     */
    setupInputHandlers() {
        const canvas = this.canvas;

        // Mouse down - start pan on left click on empty canvas
        canvas.addEventListener('mousedown', (e) => {
            if (e.button === 0) {
                // Left click = start pan (will be refined to check if clicking node)
                this.startPan(e);
            }
        });

        // Mouse move
        canvas.addEventListener('mousemove', (e) => {
            if (this.isPanning) {
                this.doPan(e);
            }
            if (this.isDrawingConnection) {
                this.updateConnectionPreview(e);
            }
        });

        // Mouse up
        canvas.addEventListener('mouseup', (e) => {
            if (this.isPanning) {
                this.endPan();
            }
        });

        // Mouse leave
        canvas.addEventListener('mouseleave', () => {
            if (this.isPanning) {
                this.endPan();
            }
        });

        // Wheel = zoom (centered at cursor)
        canvas.addEventListener('wheel', (e) => {
            e.preventDefault();
            // Adaptive zoom speed - slower when zoomed in, faster when zoomed out
            const baseSpeed = 0.0005;
            const adaptiveSpeed = baseSpeed * Math.max(0.5, this.viewport.zoom);
            const delta = -e.deltaY * adaptiveSpeed;
            this.viewport.zoomAt(delta, e.clientX, e.clientY);
        }, { passive: false });

        // Double-click to create node
        canvas.addEventListener('dblclick', (e) => {
            const worldPos = this.viewport.screenToWorld(e.clientX, e.clientY);
            eventBus.emit(Events.CANVAS_DBLCLICK, { x: worldPos.x, y: worldPos.y });
        });

        // Click - only fire click event if not panning
        canvas.addEventListener('click', (e) => {
            // Only handle as click if we didn't just finish a pan
            if (this.wasPanning) {
                this.wasPanning = false;
                return;
            }
            const worldPos = this.viewport.screenToWorld(e.clientX, e.clientY);
            eventBus.emit(Events.CANVAS_CLICK, {
                x: worldPos.x,
                y: worldPos.y,
                screenX: e.clientX,
                screenY: e.clientY,
                shiftKey: e.shiftKey
            });
        });
    }

    /**
     * Start panning
     */
    startPan(e) {
        // Cancel any ongoing momentum
        if (this.momentumAnimationId) {
            cancelAnimationFrame(this.momentumAnimationId);
            this.momentumAnimationId = null;
        }

        this.isPanning = true;
        this.panStartX = e.clientX;
        this.panStartY = e.clientY;
        this.lastPanX = e.clientX;
        this.lastPanY = e.clientY;
        this.lastPanTime = performance.now();
        this.velocityX = 0;
        this.velocityY = 0;
        this.container.classList.add('panning');
    }

    /**
     * Continue panning
     */
    doPan(e) {
        const now = performance.now();
        const dt = now - this.lastPanTime;

        const dx = e.clientX - this.lastPanX;
        const dy = e.clientY - this.lastPanY;

        // Track velocity (smoothed with previous velocity)
        if (dt > 0) {
            const instantVelX = dx / dt * 16; // Normalize to ~60fps
            const instantVelY = dy / dt * 16;
            this.velocityX = this.velocityX * 0.3 + instantVelX * 0.7;
            this.velocityY = this.velocityY * 0.3 + instantVelY * 0.7;
        }

        this.lastPanX = e.clientX;
        this.lastPanY = e.clientY;
        this.lastPanTime = now;
        this.viewport.pan(dx, dy);
    }

    /**
     * End panning
     */
    endPan() {
        // Check if we actually moved (not just a click)
        const movedX = Math.abs(this.lastPanX - this.panStartX);
        const movedY = Math.abs(this.lastPanY - this.panStartY);
        if (movedX > 3 || movedY > 3) {
            this.wasPanning = true;
        }
        this.isPanning = false;
        this.container.classList.remove('panning');

        // Apply momentum if velocity is significant
        const speed = Math.sqrt(this.velocityX ** 2 + this.velocityY ** 2);
        if (speed > 1) {
            this.applyMomentum();
        }
    }

    /**
     * Apply momentum after pan release
     */
    applyMomentum() {
        const friction = 0.92; // Deceleration factor
        const minVelocity = 0.5; // Stop threshold

        const animate = () => {
            this.velocityX *= friction;
            this.velocityY *= friction;

            const speed = Math.sqrt(this.velocityX ** 2 + this.velocityY ** 2);
            if (speed < minVelocity) {
                this.momentumAnimationId = null;
                return;
            }

            this.viewport.pan(this.velocityX, this.velocityY);
            this.momentumAnimationId = requestAnimationFrame(animate);
        };

        this.momentumAnimationId = requestAnimationFrame(animate);
    }

    /**
     * Update connection preview during drawing
     */
    updateConnectionPreview(e) {
        if (this.connectionPreview) {
            this.connectionPreview.endX = e.clientX;
            this.connectionPreview.endY = e.clientY;
            this.needsRender = true;
        }
    }

    /**
     * Resize canvas for high-DPI displays
     */
    resize() {
        const rect = this.container.getBoundingClientRect();
        const dpr = window.devicePixelRatio || 1;

        this.canvas.width = rect.width * dpr;
        this.canvas.height = rect.height * dpr;
        this.canvas.style.width = `${rect.width}px`;
        this.canvas.style.height = `${rect.height}px`;

        this.ctx.scale(dpr, dpr);

        this.viewport.resize(rect.width, rect.height);
        this.needsRender = true;
    }

    /**
     * Set the document to render
     * @param {import('../models/CanvasDocument.js').CanvasDocument} doc 
     */
    setDocument(doc) {
        this.document = doc;
        this.viewport.fromJSON(doc.viewport);
        this.showGrid = doc.showGrid;
        this.isDark = doc.theme === THEME.DARK;
        this.needsRender = true;
        this.nodesNeedUpdate = true;
    }

    /**
     * Select nodes
     * @param {string[]} nodeIds 
     * @param {boolean} addToSelection 
     */
    selectNodes(nodeIds, addToSelection = false) {
        if (!addToSelection) {
            this.selectedNodeIds.clear();
        }
        for (const id of nodeIds) {
            this.selectedNodeIds.add(id);
        }
        this.needsRender = true;
    }

    /**
     * Clear selection
     */
    clearSelection() {
        this.selectedNodeIds.clear();
        this.selectedConnectionId = null;
        this.needsRender = true;
    }

    /**
     * Mark that nodes need full re-render (call when nodes added/removed/content changed)
     */
    markNodesChanged() {
        this.nodesNeedUpdate = true;
        this.needsRender = true;
    }

    /**
     * Start render loop
     */
    startRenderLoop() {
        const loop = () => {
            if (this.needsRender) {
                this.render();
                this.needsRender = false;
            }
            this.animationFrameId = requestAnimationFrame(loop);
        };
        loop();
    }

    /**
     * Stop render loop
     */
    stopRenderLoop() {
        if (this.animationFrameId) {
            cancelAnimationFrame(this.animationFrameId);
        }
    }

    /**
     * Main render function
     */
    render() {
        const { ctx, viewport, isDark } = this;
        const { width, height } = viewport;

        // Clear canvas
        const bgColor = isDark ? '#161616' : '#FEFEFE';
        ctx.fillStyle = bgColor;
        ctx.fillRect(0, 0, width, height);

        // Draw grid
        if (this.showGrid) {
            this.gridRenderer.render(ctx, viewport, isDark);
        }

        // Cache nodes for this frame
        const nodes = this.document ? this.document.getAllNodes() : [];
        const connections = this.document ? this.document.getAllConnections() : [];
        const groups = this.document ? this.document.getAllGroups() : [];

        // Draw connections
        if (this.document) {
            this.connectionRenderer.render(
                ctx,
                connections,
                this.document.nodes,
                viewport,
                this.selectedConnectionId,
                isDark
            );
        }

        // Draw connection preview
        if (this.connectionPreview) {
            this.connectionRenderer.renderPreview(
                ctx,
                this.connectionPreview.startX,
                this.connectionPreview.startY,
                this.connectionPreview.endX,
                this.connectionPreview.endY,
                isDark
            );
        }

        // Render nodes (via HTML overlay) - only when needed
        if (this.document && this.nodesNeedUpdate) {
            this.nodeRenderer.render(nodes, viewport, this.selectedNodeIds, isDark);
            this.nodesNeedUpdate = false;
        } else if (this.document) {
            // Just update positions without re-rendering content
            this.nodeRenderer.updatePositions(nodes, viewport, this.selectedNodeIds);
        }

        // Draw selection overlays on canvas
        if (this.document) {
            const selectedNodes = nodes.filter(n => this.selectedNodeIds.has(n.id));
            if (selectedNodes.length > 0) {
                this.selectionRenderer.renderSelection(ctx, selectedNodes, viewport, isDark);
            }

            // Draw group bounds
            for (const group of groups) {
                const groupNodes = group.nodeIds
                    .map(id => this.document.getNode(id))
                    .filter(Boolean);
                if (groupNodes.length > 0) {
                    this.selectionRenderer.renderGroupBounds(ctx, groupNodes, viewport, isDark);
                }
            }
        }

        // Draw selection box
        if (this.selectionBox) {
            this.selectionRenderer.renderSelectionBox(
                ctx,
                this.selectionBox.x1,
                this.selectionBox.y1,
                this.selectionBox.x2,
                this.selectionBox.y2,
                isDark
            );
        }
    }

    /**
     * Get viewport
     * @returns {Viewport}
     */
    getViewport() {
        return this.viewport;
    }

    /**
     * Cleanup
     */
    destroy() {
        this.stopRenderLoop();
        this.nodeRenderer.clear();
    }
}
